import React from 'react';

const RELOAD_FLAG_KEY = 'chunk-load-reload-attempted';

/**
 * Detects the family of errors that a stale chunk produces after a deploy: the
 * dynamic import fails because the host serves index.html (text/html) for a
 * chunk filename that no longer exists.
 */
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading( CSS)? chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    message.includes("MIME type ('text/html')")
  );
}

/**
 * Safety net around the lazily-loaded routes. `lazyWithRetry` already reloads
 * once on a stale-chunk failure; if that reload does not fix it (or a chunk
 * error surfaces some other way) this boundary shows a friendly "reload" screen
 * instead of leaving the user on a blank page.
 *
 * Only stale-chunk errors are handled here. Any other error is re-thrown so it
 * keeps its previous behaviour (and error reporting) untouched.
 */
export default class ErrorBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    return { error };
  }

  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  // eslint-disable-next-line class-methods-use-this
  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;

    let alreadyReloaded = false;
    try {
      alreadyReloaded =
        window.sessionStorage.getItem(RELOAD_FLAG_KEY) === 'true';
    } catch {
      alreadyReloaded = false;
    }
    if (alreadyReloaded) return;

    try {
      window.sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');
    } catch {
      return;
    }
    window.location.reload();
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) return children;
    if (!isChunkLoadError(error)) throw error;

    return (
      <div className="flex h-full min-h-[60vh] w-full flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-semibold text-gray-900">
          No se ha podido cargar la aplicación
        </h1>
        <p className="max-w-md text-sm text-gray-600">
          Es posible que haya una versión nueva disponible. Vuelve a cargar la
          página para continuar.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-[#2A64DF] px-4 py-2 text-sm font-medium text-white hover:bg-[#1f4fc0]"
        >
          Recargar la página
        </button>
      </div>
    );
  }
}
